<template>
	<div class="question-answer-section">
		<div class="feedback-section-header">
			<h2>
				{{ heading }}
				<span class="badge">({{ nrQuestions }})</span>
			</h2>
			<question-answer-form v-if="writingQuestion" @cancel="writingQuestion = false" :product="product" />
			<text-button v-else-if="canWriteComment" text="Ställ en fråga" @click="writeQuestion" />
		</div>
		<div v-if="questions.length">
			<question
				v-for="question in computedQuestions"
				:userVotes="userVotes"
				:product="product"
				:question="question"
				:key="'question-' + question.id"
			/>
			<div
				v-if="totalQuestionCount > questionsPerPage"
				ref="moreLessButtons"
				class="show-more"
			>
				<a v-if="page === 1" @click="page++">
					Visa fler frågor
				</a>
				<a v-else-if="! hasMorePages" @click="page = 1">
					Visa färre frågor
				</a>
				<template v-else-if="page > 1">
					Visa
					<a @click="page++">fler</a> /
					<a @click="page = 1">färre</a>
					frågor
				</template>
			</div>
		</div>
		<template v-else>
			<hr>
			<p class="no-feedback">Inga frågor har ännu ställts på denna produkt</p>
		</template>
	</div>
</template>
<script>
import waja from 'waja';
import store from 'datastore';
import { mapGetters, mapState } from 'vuex';
import alert from 'alert';
import { fixElementToBottomOfPage, getAllVariantIds } from 'utils';
import abTest from 'abTest';

export default {
	store,
	props: {
		heading: {
			type: String,
			default: '',
		},
		product: {
			type: Object,
		},
		nrQuestions: {
			type: Number,
			default: 0,
		},
	},
	data () {
		return {
			writingQuestion: false,
			questions: [],
			totalQuestionCount: 0,
			loadedPages: {},
			userVotes: {},
			page: 1,
			// questionsPerPage: 2, ***Add this back in when testing is done and approved***
			fixElementToBottomOfPage,
			getAllVariantIds,
			abTest,
		};
	},
	methods: {
		writeQuestion () {
			if (! this.isLoggedIn) {
				alert('Du måste vara inloggad för att kunna ställa frågor!');
				return;
			}

			this.writingQuestion = true;
		},
		/**
		* Check if a user has posted a question to a specific product.
		*/
		hasPostedQuestion (user, questions, product) {
			if (! questions) {
				return false;
			}

			const res = questions.find(question => {
				if (! question.user) {
					return false;
				}
				// If the product is a group product.
				if (product.variants) {
					return (question.user.id === user.id)
					&& (question.entityId === product.id);
				}
				return (question.user.id === user.id);
			});
			return res || false;
		},
		getQuestions() {
			const data = {
				commentType: 'QUESTION',
				entityIds: this.productIds,
				page: this.page,
			};

			waja.get('comment')
				.data(data)
				.on('success', res => {
					this.questions = this.questions.concat(res.data.comments);
					this.totalQuestionCount = res.data.totalCommentCount;
					this.$emit('loaded', {
						nrQuestions: res.data.totalCommentCount,
						status: res.status,
					});

					this.loadedPages[this.page] = true;
					// Fetch votes for the comments.
					if (this.isLoggedIn) {
						waja.get('question/votes')
							.data({ productIds: this.productIds })
							.on('success', res => {
								this.userVotes = Object.assign({}, this.userVotes, res.data.userQuestionVotes);
							}).go();
						this.orderQuestionsByVotes();
					}
				})
				.on('error', res => {
					this.$emit('faIled', {
						nrQuestions: 0,
						status: res.status,
					});
				}).go();
		},
		orderQuestionsByVotes () {
			// Sort questions with answers on most upvotes - downvotes;
			this.questions = this.questions.sort((q1, q2) => {
				let q1TotalPoints = q1.upvotes - q1.downvotes;
				let q2TotalPoints = q2.upvotes - q2.downvotes;
				// Map over question answers to get all votes.
				q1.replies.map(q1Reply => {
					q1TotalPoints += q1Reply.upvotes - q1Reply.downvotes;
				});
				q2.replies.map(q2Reply => {
					q2TotalPoints += q2Reply.upvotes - q2Reply.downvotes;
				});
				return (q1TotalPoints < q2TotalPoints) ? 1 : -1;
			});
		},
		scrollPage () {
			// Wait until the computed questions have been computed.
			Vue.nextTick(() => {
				// Check if we need to scroll.
				if (this.totalQuestionCount > this.questionsPerPage) {
					// Scroll the page so the show more/less buttons sit at the bottom of the page.
					fixElementToBottomOfPage(this.$refs.moreLessButtons);
				}
			});
		},
	},
	computed: {
		questionsPerPage () {
			return this.abTest.shorterReviews > 0 ? 2 : 5;
		},
		userHasPostedQuestion () {
			if (! this.isLoggedIn) {
				return false;
			}
			return this.hasPostedQuestion(this.user, this.questions, this.product);
		},
		computedQuestions () {
			return this.questions.slice(0, this.questionsPerPage * this.page);
		},
		hasMorePages () {
			return this.computedQuestions.length < this.totalQuestionCount;
		},
		/**
		 * Ids of products to fetch comments for.
		 */
		productIds () {
			return getAllVariantIds(this.product, false);
		},
		...mapGetters({
			canWriteComment: 'comments/canWriteComment',
			isLoggedIn: 'user/isLoggedIn',
		}),
		...mapState({
			user: state => state.user,
		}),
	},
	mounted () {
		this.getQuestions();
	},
	watch: {
		page (newPage, oldPage) {
			if (newPage > oldPage && ! this.loadedPages[newPage]) {
				this.getQuestions();
			}

			if (newPage === 1) {
				this.scrollPage();
			}
		},
		product() {
			this.questions = [];
			this.getQuestions();
		},
	},
};
</script>
